import { navigationRef, NavigationService } from '@/navigations';
import { useSessionStore } from '@/stores';
import React, { useEffect, type ComponentType } from 'react';
import { BackHandler, StyleSheet, View } from 'react-native';
import { useShallow } from 'zustand/shallow';

import type { RootStackParamList } from '@/navigations/types';

const redirectGateStyles = StyleSheet.create({
  flex: { flex: 1 },
});

const resetAuthHomeParams: RootStackParamList['Auth'] = {
  screen: 'AuthDrawer',
  params: {
    screen: 'HomeTabNavigator',
    params: { screen: 'Home', params: { screen: 'Dashboard' } },
  },
};

/**
 * While redirecting away from a protected tree, rendering `null` leaves the native stack
 * with no React subtree; on Android the hardware back button can then finish the activity.
 * A full-screen placeholder plus consuming back presses avoids that race.
 */
function NavigationRedirectGate() {
  useEffect(() => {
    const sub = BackHandler.addEventListener('hardwareBackPress', () => true);
    return () => sub.remove();
  }, []);
  return <View style={redirectGateStyles.flex} />;
}

/**
 * Wraps a screen component to require authentication.
 * If the user is not authenticated: resets the root navigator to UnAuth (EnterEmail).
 *
 * @example
 * const HomeScreen = withAuth(function HomeScreen() {
 *   return <View>...</View>;
 * });
 *
 * @example
 * export const ProfileScreen = withAuth(ProfileScreenBase);
 */
export function withAuth<P extends object>(
  WrappedComponent: ComponentType<P>,
  options?: {
    skipRedirect?: boolean;
  },
) {
  const skipRedirect = options?.skipRedirect ?? false;

  function WithAuthenScreen(props: P) {
    const isAuthenticated = useSessionStore(useShallow(state => state.isAuthenticated));

    useEffect(() => {
      if (isAuthenticated || skipRedirect) return;

      let cancelled = false;
      let retryId: ReturnType<typeof setTimeout> | undefined;

      const timeoutId = setTimeout(() => {
        if (cancelled) return;
        if (navigationRef.isReady()) {
          NavigationService.resetRoot('UnAuth', { screen: 'EnterEmail' });
          return;
        }
        retryId = setTimeout(() => {
          if (cancelled) return;
          if (navigationRef.isReady()) {
            NavigationService.resetRoot('UnAuth', { screen: 'EnterEmail' });
          }
        }, 100);
      }, 0);

      return () => {
        cancelled = true;
        clearTimeout(timeoutId);
        if (retryId) clearTimeout(retryId);
      };
    }, [isAuthenticated]);

    if (!isAuthenticated) {
      return <NavigationRedirectGate />;
    }

    return <WrappedComponent {...props} />;
  }

  WithAuthenScreen.displayName = `WithAuthen(${
    WrappedComponent.displayName ?? WrappedComponent.name ?? 'Screen'
  })`;

  return WithAuthenScreen;
}

/**
 * Wraps a screen component to require authentication.
 * If the user is authenticated: resets the root navigator to Auth (home dashboard).
 *
 * @example
 * const HomeScreen = withUnAuth(function HomeScreen() {
 *   return <View>...</View>;
 * });
 *
 * @example
 * export const ProfileScreen = withUnAuth(ProfileScreenBase);
 */
export function withUnAuth<P extends object>(
  WrappedComponent: ComponentType<P>,
  options?: {
    skipRedirect?: boolean;
  },
) {
  function WithUnAuthScreen(props: P) {
    const isAuthenticated = useSessionStore(useShallow(state => state.isAuthenticated));

    useEffect(() => {
      if (isAuthenticated && !options?.skipRedirect) {
        if (navigationRef.isReady()) {
          NavigationService.resetRoot('Auth', resetAuthHomeParams);
          return;
        }
      }
    }, [isAuthenticated]);

    if (isAuthenticated) {
      return null;
    }

    return <WrappedComponent {...props} />;
  }

  WithUnAuthScreen.displayName = `WithUnAuth(${
    WrappedComponent.displayName ?? WrappedComponent.name ?? 'Screen'
  })`;

  return WithUnAuthScreen;
}
